import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Popover from "@mui/material/Popover";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import IosShareIcon from "@mui/icons-material/IosShare";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";

import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_post_set_form_public_share,
  API_get_form_public_submissions,
} from "../../../constants";
import FormSubmissionReviewModal from "../training_overview/FormSubmissionReviewModal";
import FormPublicReportModal from "./FormPublicReportModal";
import QrCodePopover from "./QrCodePopover";
import { isDisplayOnlyType } from "./formFieldHelpers";
import { resolvePiping } from "./formLogic";
import { fmtDate, downloadCsv } from "./formCsvHelpers";

const reviewModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 680,
  maxWidth: "95vw",
  maxHeight: "85vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  overflowY: "auto",
  border: "none",
};

const selectSx = {
  height: 32,
  fontSize: 12,
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a78bfa" },
  // violet-700, matching every other focused control in the builder (MUI would
  // otherwise paint the theme's default blue here).
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
};

const shareLabel = (sh) => sh?.label || "Default link";

/**
 * Public-link controls for a form, rendered in the form editor header:
 *   - Links: a table of named public links (one per store, say). Each has its
 *     own URL, on/off, "ask for name & email", opens/responses, regenerate.
 *   - Responses: anonymous public submissions (all links or one), reusing
 *     FormSubmissionReviewModal to view one, with a complete CSV export.
 *   - Report: per-question aggregates, filterable by link and by categorical
 *     answers (FormPublicReportModal).
 * Only meaningful for employee/self-fill forms.
 */
function FormPublicShareControls({
  companyId,
  formUuid,
  formName,
  fields,
  initialShares,
  hasUnsavedChanges,
  canSaveForm = true,
  savingForm,
  onSaveForm,
  onSharesChange,
  // "survey" in the Engagement editor, "form" in Training; copy only.
  nounLabel = "form",
}) {
  const [shares, setShares] = React.useState(initialShares || []);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [savingUuid, setSavingUuid] = React.useState(null); // uuid | "new" | null
  const [newLabel, setNewLabel] = React.useState("");
  const [addingLink, setAddingLink] = React.useState(false);

  const [responsesOpen, setResponsesOpen] = React.useState(false);
  const [responsesShare, setResponsesShare] = React.useState(""); // "" = all
  const [subs, setSubs] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [subsLoading, setSubsLoading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [reviewSub, setReviewSub] = React.useState(null);

  const [reportOpen, setReportOpen] = React.useState(false);

  const PAGE_SIZE = 500;

  React.useEffect(() => {
    setShares(initialShares || []);
  }, [initialShares]);

  // The editor is reused across forms; link-scoped response state must not
  // leak from one form to the next.
  React.useEffect(() => {
    setResponsesShare("");
    setSubs([]);
    setTotal(0);
    setReviewSub(null);
    setResponsesOpen(false);
    setReportOpen(false);
    setAnchorEl(null);
    setAddingLink(false);
    setNewLabel("");
  }, [formUuid]);

  const publicUrl = (sh) =>
    sh?.share_token ? `${window.location.origin}/f/${sh.share_token}` : null;

  const anyEnabled = shares.some((sh) => sh.is_enabled);
  const responseCount = shares.reduce((n, sh) => n + (sh.submission_count || 0), 0);

  // Create or update a link. `share_uuid` absent = create.
  const callShare = async (patch, shareUuid) => {
    setSavingUuid(shareUuid || "new");
    try {
      const target = shareUuid ? shares.find((sh) => sh.uuid === shareUuid) : null;
      const body = {
        company_id: companyId,
        learning_form_uuid: formUuid,
        is_enabled:
          patch.is_enabled !== undefined
            ? patch.is_enabled
            : target
            ? target.is_enabled
            : true,
      };
      if (shareUuid) body.share_uuid = shareUuid;
      if (patch.regenerate) body.regenerate = true;
      if (patch.collect_respondent_info !== undefined) {
        body.collect_respondent_info = patch.collect_respondent_info;
      }
      if (patch.label !== undefined) body.label = patch.label;
      const res = await postRightworkAPI(API_post_set_form_public_share, body);
      const payload = res?.data?.redux?.payload;
      if (Array.isArray(payload?.public_shares)) {
        setShares(payload.public_shares);
        // Lift to the editor: these controls unmount when "completed by" flips
        // to manager, so the parent must hold the current links or a remount
        // would seed from a stale list.
        if (onSharesChange) onSharesChange(payload.public_shares);
      }
      return true;
    } catch (e) {
      const message = e?.response?.data?.system?.message;
      toast.error(message || "Couldn't update the share link. Please try again.");
      return false;
    } finally {
      setSavingUuid(null);
    }
  };

  const copyLink = (sh) => {
    const url = publicUrl(sh);
    if (!url) return;
    try {
      navigator.clipboard?.writeText(url);
      toast.success("Link copied", { autoClose: 2000 });
    } catch (e) {
      toast.error("Couldn't copy. Select and copy the link manually.");
    }
  };

  const handleAddLink = async () => {
    const ok = await callShare({ is_enabled: true, label: newLabel.trim() }, null);
    if (ok) {
      setNewLabel("");
      setAddingLink(false);
      toast.success("Public link created.", { autoClose: 2000 });
    }
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Responses

  const fetchPage = async (offset, shareUuid, before) => {
    const params = {
      company_id: companyId,
      learning_form_uuid: formUuid,
      offset,
      limit: PAGE_SIZE,
    };
    if (shareUuid) params.share_uuid = shareUuid;
    if (before) params.before = before;
    const res = await getRightworkAPI(API_get_form_public_submissions, params);
    return res?.status === 200 ? res.data?.redux?.payload : null;
  };

  // Stale-fetch guard: the link filter can change while a page is in flight;
  // only the latest request may write subs/total/loading.
  const responsesSeq = React.useRef(0);
  const loadResponses = async (shareUuid) => {
    const seq = ++responsesSeq.current;
    setSubsLoading(true);
    try {
      const payload = await fetchPage(0, shareUuid);
      if (seq !== responsesSeq.current) return;
      setSubs(payload?.submissions || []);
      setTotal(payload?.total ?? (payload?.submissions || []).length);
    } catch (e) {
      if (seq !== responsesSeq.current) return;
      setSubs([]);
      setTotal(0);
    } finally {
      if (seq === responsesSeq.current) setSubsLoading(false);
    }
  };

  const openResponses = () => {
    setResponsesOpen(true);
    loadResponses(responsesShare);
  };

  const changeResponsesShare = (uuid) => {
    setResponsesShare(uuid);
    loadResponses(uuid);
  };

  // Walk every page so the CSV never silently omits older rows. Stop on a short
  // page (don't trust a possibly-shifting total). The only bound is an
  // infinite-loop guard far above any real form; if it ever trips, say so
  // rather than silently truncating.
  const EXPORT_HARD_LIMIT = 2000000;
  const fetchAllForExport = async () => {
    const all = [];
    let offset = 0;
    // Fixed cutoff so rows arriving mid-export can't shift the pages. Taken
    // from the SERVER clock on the first page (browser clocks can be skewed);
    // the first page has no cutoff, so everything it returned is <= server_now.
    let before = null;
    for (;;) {
      const payload = await fetchPage(offset, responsesShare, before);
      const page = payload?.submissions || [];
      if (!before && payload?.server_now) before = payload.server_now;
      all.push(...page);
      if (page.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
      if (offset >= EXPORT_HARD_LIMIT) {
        toast.warning(
          `Export stopped at ${all.length.toLocaleString()} responses; the file is incomplete.`,
          { autoClose: 6000 }
        );
        break;
      }
    }
    return all;
  };

  // Map an anonymous public submission onto the shape FormSubmissionReviewModal
  // expects (it prefers the submission's own field snapshot).
  const toReview = (s) => ({
    fields: s.fields_snapshot,
    responses: s.responses,
    employee_name: s.respondent_name || "Anonymous",
    date_completed: s.created_at,
    is_passed: s.is_passed,
    score: s.score,
  });

  const exportCsv = async () => {
    if (!subs.length || exporting) return;
    setExporting(true);
    let data = subs;
    try {
      if (total > subs.length) data = await fetchAllForExport();
    } catch (e) {
      toast.error("Couldn't load all responses for export. Please try again.");
      setExporting(false);
      return;
    }
    if (!data.length) {
      setExporting(false);
      return;
    }
    // Column union across submission snapshots, keyed by field id + label.
    // Forms get edited over time, so a field renamed mid-collection must become
    // a SEPARATE column per label — otherwise older submissions (whose snapshot
    // kept the old label) would be exported under a newer row's label, corrupting
    // the historical answers. Iterate oldest-first so earlier labels lead, and
    // record each submission's own id→label map to route its answers correctly.
    // A column header serves every row, so there's no one respondent to pipe
    // from: a token reads "[answer]", as it does in the report and the response
    // viewer. Column identity still keys off the RAW label, so two questions
    // whose piped labels happen to read alike stay separate columns.
    const pipe = (text) => resolvePiping(text, {}, {}, "[answer]");
    const fieldCols = [];
    const seenCols = new Set();
    const labelMapBySub = new Map();
    [...data].reverse().forEach((s) => {
      const labelMap = {};
      (s.fields_snapshot || []).forEach((f) => {
        if (isDisplayOnlyType(f.type) || !f.id) return;
        const label = f.label || f.id;
        labelMap[f.id] = label;
        const key = `${f.id} ${label}`;
        if (!seenCols.has(key)) {
          seenCols.add(key);
          fieldCols.push({ id: f.id, label, header: pipe(label) });
        }
      });
      labelMapBySub.set(s.uuid, labelMap);
    });
    const header = [
      "Submitted",
      "Link",
      "Name",
      "Email",
      "Score",
      "Passed",
      ...fieldCols.map((c) => c.header),
    ];
    const fmtValue = (v) => {
      if (v === true) return "Yes";
      if (v === false) return "No";
      return v;
    };
    const rows = data.map((s) => {
      const labelMap = labelMapBySub.get(s.uuid) || {};
      return [
        fmtDate(s.created_at),
        s.share_label || "Default link",
        s.respondent_name || "",
        s.respondent_email || "",
        s.score === null || s.score === undefined ? "" : s.score,
        s.is_passed === true ? "Yes" : s.is_passed === false ? "No" : "",
        ...fieldCols.map((c) =>
          labelMap[c.id] === c.label ? fmtValue(s.responses?.[c.id]) : ""
        ),
      ];
    });
    downloadCsv(
      [header, ...rows],
      `${(formName || "form").replace(/[^a-z0-9]+/gi, "_")}_responses.csv`
    );
    setExporting(false);
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Render

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setReportOpen(true)}
          disabled={!formUuid}
          className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-50"
          title="Aggregate report of public responses"
        >
          <BarChartOutlinedIcon style={{ fontSize: 18 }} />
          Report
        </button>
        <button
          type="button"
          onClick={openResponses}
          disabled={!formUuid}
          className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-50"
        >
          <InsertDriveFileOutlinedIcon style={{ fontSize: 18 }} />
          Responses{responseCount ? ` (${responseCount})` : ""}
        </button>
        <button
          type="button"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          title="Public share links"
          className={[
            "inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
            anyEnabled
              ? "bg-emerald-100 hover:bg-emerald-200 text-emerald-700"
              : "bg-slate-100 hover:bg-slate-200 text-slate-700",
          ].join(" ")}
        >
          <IosShareIcon style={{ fontSize: 18 }} />
          Share{shares.length > 1 ? ` (${shares.length})` : ""}
        </button>
      </div>

      {/* Links popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { borderRadius: "12px", mt: 1 } } }}
      >
        <div className="w-[520px] max-w-[95vw] p-4">
          <div className="text-sm font-semibold text-slate-900">Public share links</div>
          <p className="mt-1 text-xs text-slate-500">
            Anyone with a link can respond, no login. Names aren't collected unless
            you ask for name &amp; email. Make one link per store or team to see
            results by group.
          </p>

          {hasUnsavedChanges && (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
              <div className="text-xs text-amber-800">
                You have unsaved changes. Save the {nounLabel} for them to appear in
                the public links.
              </div>
              <button
                type="button"
                disabled={savingForm || !canSaveForm}
                onClick={onSaveForm}
                className="mt-1.5 text-xs font-semibold text-amber-900 underline disabled:opacity-60 disabled:no-underline"
              >
                {savingForm ? "Saving…" : `Save ${nounLabel}`}
              </button>
              {!canSaveForm && (
                <div className="mt-1 text-xs text-amber-700">
                  Resolve the errors in the {nounLabel} first.
                </div>
              )}
            </div>
          )}

          {!formUuid ? (
            <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500">
              Save the {nounLabel} first to create a share link.
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {shares.map((sh) => (
                <ShareRow
                  key={sh.uuid}
                  share={sh}
                  url={publicUrl(sh)}
                  saving={savingUuid === sh.uuid}
                  onCopy={() => copyLink(sh)}
                  onToggle={(on) => callShare({ is_enabled: on }, sh.uuid)}
                  onCollect={(v) => callShare({ collect_respondent_info: v }, sh.uuid)}
                  onRegenerate={() =>
                    callShare({ is_enabled: true, regenerate: true }, sh.uuid).then(
                      (ok) =>
                        ok &&
                        toast.success("New link generated. The old link no longer works.", {
                          autoClose: 3000,
                        })
                    )
                  }
                  onRename={(label) => callShare({ label }, sh.uuid)}
                />
              ))}

              {addingLink ? (
                <div className="flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2">
                  <input
                    type="text"
                    autoFocus
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddLink();
                      if (e.key === "Escape") setAddingLink(false);
                    }}
                    placeholder="Link name, e.g. Tavolàta - Fremont"
                    className="flex-1 min-w-0 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 outline-none transition-colors focus:border-violet-700"
                  />
                  <button
                    type="button"
                    disabled={savingUuid === "new"}
                    onClick={handleAddLink}
                    className="shrink-0 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-xs font-medium px-3 py-1.5 disabled:opacity-60"
                  >
                    {savingUuid === "new" ? "Creating…" : "Create"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddingLink(false)}
                    className="shrink-0 text-xs text-slate-500 hover:text-slate-800"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingLink(true)}
                  className={[
                    "w-full rounded-lg text-sm font-medium py-2",
                    shares.length
                      ? "border border-dashed border-slate-300 text-slate-600 hover:border-violet-400 hover:text-violet-700"
                      : "bg-violet-700 hover:bg-violet-800 text-white",
                  ].join(" ")}
                >
                  {shares.length ? "+ Add another link" : "Create public link"}
                </button>
              )}
            </div>
          )}
        </div>
      </Popover>

      {/* Responses list */}
      <Modal
        open={responsesOpen}
        onClose={() => setResponsesOpen(false)}
        disableEnforceFocus
      >
        <Box sx={reviewModalStyle}>
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10 rounded-t-2xl flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-slate-900">Responses</h3>
              <p className="text-sm text-slate-600 truncate">{formName} · public links</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {shares.length > 1 && (
                <Select
                  value={responsesShare}
                  onChange={(e) => changeResponsesShare(e.target.value)}
                  size="small"
                  displayEmpty
                  sx={{ ...selectSx, minWidth: 150 }}
                >
                  <MenuItem value="" sx={{ fontSize: 12 }}>
                    All links
                  </MenuItem>
                  {shares.map((sh) => (
                    <MenuItem key={sh.uuid} value={sh.uuid} sx={{ fontSize: 12 }}>
                      {shareLabel(sh)}
                    </MenuItem>
                  ))}
                </Select>
              )}
              <button
                type="button"
                onClick={exportCsv}
                disabled={!subs.length || exporting}
                className="px-3 py-1.5 text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium disabled:opacity-50"
              >
                {exporting ? "Exporting…" : "Export CSV"}
              </button>
              <button
                className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"
                onClick={() => setResponsesOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          <div className="px-6 py-4">
            {!subsLoading && total > subs.length && (
              <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Showing the most recent {subs.length} of {total} responses. Export
                CSV includes all {total}.
              </div>
            )}
            {subsLoading ? (
              <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
            ) : subs.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">
                No responses yet.
              </div>
            ) : (
              <div className="flex flex-col divide-y divide-slate-100">
                {subs.map((s) => (
                  <button
                    key={s.uuid}
                    type="button"
                    onClick={() => setReviewSub(s)}
                    className="flex items-center justify-between py-3 text-left hover:bg-slate-50 px-2 -mx-2 rounded-lg gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {s.respondent_name || "Anonymous"}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {s.respondent_email ? `${s.respondent_email} · ` : ""}
                        {fmtDate(s.created_at)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {shares.length > 1 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 max-w-[140px] truncate">
                          {s.share_label || "Default link"}
                        </span>
                      )}
                      {/* Use the submission's OWN stored score, not the form's
                          current scoring state — historical scored rows must keep
                          showing pass/fail even if scoring was later disabled. */}
                      {s.score !== null && s.score !== undefined && (
                        <div className="text-xs tabular-nums text-slate-600">
                          {s.score}
                          {s.is_passed === true
                            ? " · Passed"
                            : s.is_passed === false
                            ? " · Not passed"
                            : ""}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Box>
      </Modal>

      <FormSubmissionReviewModal
        open={!!reviewSub}
        onClose={() => setReviewSub(null)}
        record={{
          name: formName,
          // Scoring state from THIS submission's snapshot, not the form's
          // current setting — so a row scored at submit time still renders
          // pass/fail even if scoring was later turned off or edited.
          scoring_enabled: !!reviewSub?.scoring_snapshot?.scoring_enabled,
          fields: fields || [],
        }}
        submission={reviewSub ? toReview(reviewSub) : null}
      />

      <FormPublicReportModal
        key={formUuid || "new"}
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        companyId={companyId}
        formUuid={formUuid}
        formName={formName}
        shares={shares}
      />
    </>
  );
}

// One link row in the Links popover: editable label, URL + copy, counts, and
// the per-link controls (name & email, on/off, regenerate).
function ShareRow({ share, url, saving, onCopy, onToggle, onCollect, onRegenerate, onRename }) {
  const [editingLabel, setEditingLabel] = React.useState(false);
  const [labelDraft, setLabelDraft] = React.useState(share.label || "");
  React.useEffect(() => setLabelDraft(share.label || ""), [share.label]);

  const commitLabel = () => {
    setEditingLabel(false);
    if ((labelDraft || "").trim() !== (share.label || "")) onRename(labelDraft.trim());
  };

  return (
    <div
      className={[
        "rounded-lg border px-3 py-2",
        share.is_enabled ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50",
      ].join(" ")}
    >
      <div className="flex items-center gap-2">
        {editingLabel ? (
          <input
            type="text"
            autoFocus
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitLabel();
              if (e.key === "Escape") {
                setLabelDraft(share.label || "");
                setEditingLabel(false);
              }
            }}
            placeholder="Link name"
            className="flex-1 min-w-0 rounded border border-slate-300 px-1.5 py-0.5 text-sm font-medium text-slate-900 outline-none transition-colors focus:border-violet-700"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingLabel(true)}
            title="Rename link"
            className="flex-1 min-w-0 text-left text-sm font-medium text-slate-900 truncate hover:text-violet-700"
          >
            {shareLabel(share)}
          </button>
        )}
        <span
          className={[
            "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0",
            share.is_enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600",
          ].join(" ")}
        >
          {share.is_enabled ? "On" : "Off"}
        </span>
        <span className="text-[11px] text-slate-500 shrink-0 tabular-nums">
          {share.open_count || 0} {share.open_count === 1 ? "open" : "opens"} ·{" "}
          {share.submission_count || 0}{" "}
          {share.submission_count === 1 ? "response" : "responses"}
        </span>
      </div>

      {share.is_enabled && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="text"
            readOnly
            value={url || ""}
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-700 outline-none transition-colors focus:border-violet-700"
          />
          <button
            type="button"
            onClick={onCopy}
            className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-xs font-medium px-2.5 py-1"
          >
            <ContentCopyIcon style={{ fontSize: 14 }} />
            Copy
          </button>
          <QrCodePopover url={url} label={shareLabel(share)} />
        </div>
      )}

      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={!!share.collect_respondent_info}
            disabled={saving}
            onChange={(e) => onCollect(e.target.checked)}
            className="accent-violet-600 w-3.5 h-3.5"
          />
          Ask for name &amp; email
        </label>
        <div className="flex items-center gap-3">
          {share.is_enabled && (
            <button
              type="button"
              disabled={saving}
              onClick={onRegenerate}
              className="text-[11px] font-medium text-slate-600 hover:text-slate-900 disabled:opacity-60"
            >
              Regenerate
            </button>
          )}
          <button
            type="button"
            disabled={saving}
            onClick={() => onToggle(!share.is_enabled)}
            className={[
              "text-[11px] font-medium disabled:opacity-60",
              share.is_enabled
                ? "text-red-600 hover:text-red-700"
                : "text-violet-700 hover:text-violet-800",
            ].join(" ")}
          >
            {saving ? "Saving…" : share.is_enabled ? "Turn off" : "Turn on"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FormPublicShareControls;
